import React, { useContext, useMemo, useEffect } from "react";
import Title from "../../../components/Title";
import LineGraph from "../../../components/ResponsiveLine";
import { gql, useLazyQuery } from "@apollo/client";
import AppContext, { IFilters } from "../../../AppContext";
import { Entorno } from "../../../utils/constants";
import {
  getMax,
  getMedidaSQL,
  getMin,
  getSteps,
  mergeKeyData,
} from "../../../utils/functions";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { GraphNotCompatible } from "../../../components/GraphNotCompatible";
import "./style.scss";
import { useTranslation } from "react-i18next";

export const titleEn = "Market share";
export const title = `Cuota de mercado`;
export const filters: IFilters = {
  limitedPeriodo: true,
  showMedidas: true,
  hasTicketMedio: false,
  isDesglose: false,
  showEntorno: true,
  showSegmentos: true,
  showMyData: false,
  showCanal: false,
  showVariable: true,
};

const Graph = () => {
  const { t } = useTranslation();
  const { semanas, filters, user } = useContext(AppContext);
  let currentFilters = React.useRef<any>(null);

  const medida = getMedidaSQL(filters.medidas);
  let entorno = filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";

  let leverTypeKeys: any = ["Totales", "Canal", "Momento", "Region", "Ubicacion"];
  let leverKeys: any = {
    Canal: ["Total", "Local", "TakeAway", "Delivery"],
    Momento: [
      "Total",
      "Mañanas",
      "Tardes",
      "Comidas (L-J)",
      "Cenas (L-J)",
      "Comidas (V-D)",
      "Cenas (V-D)",
    ],
    Region: ["Total", "Z1", "Z2", "Z3", "Z4", "Z5", "Z6"],
    Ubicacion: ["Total", "Calle", "CC", "Resto"],
  };

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
      $leverType: String
      $lever: String
    ) {
      graph(
        Graph: "Mi cuota de mercado"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: {
          Variables: $var
          Segmento: $segment
          SegmentType: $segmentType
          LeverType: $leverType
          Lever: $lever
        }
      )
    }
  `;

  const [getData, { loading, data, error }] = useLazyQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    let leverTypeStr = ``;
    let leverStr = ``;

    getData({
      variables: {
        yr1: semanas[0].year,
        wk1: semanas[0].week,
        yr2: semanas[1].year,
        wk2: semanas[1].week,
        var: 1,
        segment:
          filters.segmentType === 0
            ? filters?.segmentos[filters?.segment]
            : filters?.especialidades[filters?.segment],
        segmentType: filters.segmentType,
        leverType: leverTypeStr || "",
        lever: leverStr || "",
      },
    });
    currentFilters.current = { ...filters };
  }, [filters.segmentType, filters.segment, semanas]);

  let errorMsg = t("An-error-has-occur-when-recovering-data");
  let isLoading = loading || data === undefined;
  let isError = !semanas || !!error || medida === "TicketMedio";

  let labels: string[] = [],
    datalabels: string[] = [],
    values: number[][] = [],
    series: any = [];

  let exportData: any[] = [];

  if (!loading && !error && data) {
    try {
      const myData = data?.graph;

      if (!myData) throw new Error(errorMsg);
      if (myData?.Error) throw new Error(`${data.Error}`);

      labels = myData.map((d: any) => Object.keys(d)[0]);
      if (labels.length < 3)
        throw new Error(
          t("There-is-not-enough-data-to-show-results-for-this-filter-selection")
        );

      datalabels = myData[0][labels[0]].map(
        (d: { Descripcion: string }) => d.Descripcion
      );

      for (let i = 0; i < datalabels.length; i++) {
        values.push(
          myData
            .map((dat: any) =>
              Object.values(dat).map((d: any) => {
                if (filters?.medidas === 2 && d[i]?.Item[medida] === undefined) {
                  throw new Error(t("Average-ticket-not-aviable"));
                } else {
                  if (filters.leverType === 0) {
                    let object;
                    if (d[i]?.Descripcion === datalabels[i]) {
                      object = d[i];
                    } else {
                      object = d.find((item: any) => item?.Descripcion === datalabels[i]);
                    }
                    return object?.Item[medida][entorno]["Totales"];
                  } else {
                    let leverKey = leverTypeKeys[filters.leverType || 0];
                    let leverSubKey = leverKeys[leverKey][filters.lever || 0];
                    return d[i]?.Item[medida][entorno][leverKey][leverSubKey];
                  }
                }
              })
            )
            .flat()
            .reverse()
        );
      }
      labels.reverse();

      values.forEach((val, i) => {
        let name = datalabels[i];
        const normalized = name
          .trim()
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\s+/g, " ");
        if (normalized === "mercado sin mi") {
          name = t("Market-excluding-me");
        } else if (normalized === "mercado") {
          name = t("Market");
        } else if (t(name) !== name) {
          name = t(name);
        }
        series.push({
          name,
          data: val,
        });
      });

      currentFilters.current = { ...filters };
      exportData = getExportData(
        myData,
        datalabels,
        labels,
        semanas[0].year === 2019,
        currentFilters.current,
        leverTypeKeys,
        leverKeys,
        t
      );
    } catch (err: any) {
      errorMsg = err.message ?? errorMsg;
    }
  }

  const max: number | undefined = useMemo(() => getMax([...values]), [values]);
  const min: number | undefined = 0;
  const stepSize = useMemo(() => getSteps(min, max), [min, max]);

  const graphData = {
    datalabels: datalabels,
    labels: labels,
    values: values,
    colors: [
      "#A30041",
      "#85b709",
      "#363aaf",
      "#9a2d79",
      "#4d947a",
      "#7EA3CF",
      "#DC9E9D",
    ],
    annotation: [],
    series: series,
    min: 0,
    max: max,
    stepSize: stepSize,
  };

  const emptyData = {
    labels: [],
    datasets: [],
  };

  return (
    <div className="chart-container">
      <Title
        title={t("Market-Share")}
        error={!!error}
        files={{ data: exportData, semanas, user, title: t("Market-share"), leyenda: t("Period") }}
      />
      <div className="chart">
        {!isError && (
          <LineGraph
            data={
              isError ||
              (filters.entorno === Entorno.Comparable && filters.leverType > 0)
                ? emptyData
                : graphData
            }
            hue={320}
            annotation={"#00ff00"}
            percentages
            error={error}
          />
        )}
      </div>
      {filters.entorno === Entorno.Comparable &&
        filters.leverType > 0 &&
        !loading && (
          <GraphNotCompatible>
            {t("Graph-not-support")}
          </GraphNotCompatible>
        )}
      {isLoading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
          <p>{t("Extracting-data")}</p>
        </div>
      )}
      {isError && <GraphNotCompatible>{errorMsg}</GraphNotCompatible>}
    </div>
  );
};

export default Graph;


const translateTopLabel = (label: string, t: (k: string) => string) => {
  if (!label) return label;
  const norm = label.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (norm === "mercado") return t("Market") as string;
  if (norm === "mercado sin mi") return t("Market-excluding-me") as string;
  return t(label) !== label ? (t(label) as string) : label;
};

const translateLeverPathLabel = (leverKey: string, subKey: string, t: (k: string) => string) => {
  
  const mapDirect: Record<string, string> = {
    Calle: t("Street"),
    CC: t("Mall"),
    Resto: t("Others"),
    Local: t("On-premises"),
    TakeAway: t("Take-away"),
    Delivery: t("Delivery"),
    "Mañanas": t("Morning"),
    "Tardes": t("Afternoon"),
    "Comidas (L-J)": t("Weekday lunch (M-T)"),
    "Comidas (V-D)": t("Weekend lunch (F-S)"),
    "Cenas (L-J)": t("Weekday dinner (M-T)"),
    "Cenas (V-D)": t("Weekend dinner (F-S)"),
  };
  if (mapDirect[subKey]) return mapDirect[subKey];


  if (/^Z[0-6]$/.test(subKey)) return subKey;

  return t(subKey) !== subKey ? (t(subKey) as string) : subKey;
};

const getExportData = (
  data: any,
  datalabels: any[],
  labels: any,
  isLeap: boolean,
  currentFilters: any,
  leverTypeKeys: any,
  leverKeys: any,
  t: (k: string) => string
) => {
  const exportData: any[] = [];

  
  const scopes: string[] = ["Totales"];
  if (!isLeap && currentFilters.leverType === 0) scopes.push("Comparables");

  
  const measureMap: Record<string, string> = {
    Ventas: t("Sales"),
    Tickets: t("Tickets"),
  };
  const scopeMap: Record<string, string> = {
    Totales: t("Totals"),
    Comparables: t("Comparables"),
  };

  
  const translatedDatalabels = datalabels.map((dl: string) => translateTopLabel(dl, t));

 
  const firstBlock: any = Object.values(data[0]);
  const descriptions: string[] = firstBlock[0].map((item: any) => item?.Descripcion);

  for (let i = 0; i < descriptions.length; i++) {
    const tempVals: any = {};

    ["Ventas", "Tickets"].forEach((med: string) => {
      scopes.forEach((ent: string) => {
        let tempVal: any[] = [];

        if (currentFilters.leverType === 0) {
          tempVal = data
            .map((dat: any) => Object.values(dat).map((d: any) => d[i]?.Item[med]?.[ent]))
            .flat()
            .reverse();
        } else {
          const palanca = leverTypeKeys[currentFilters.leverType];
          const subPalanca = leverKeys[palanca][currentFilters.lever];

          tempVal = data
            .map((dat: any) =>
              Object.values(dat).map(
                (d: any) => d[i]?.Item[med]?.[ent]?.[palanca]?.[subPalanca]
              )
            )
            .flat()
            .reverse();
        }

       
        const colName = `${measureMap[med]} ${scopeMap[ent]}`;
        tempVals[colName] = { ...mergeKeyData(labels, tempVal) };
      });
    });

   
    const topKeyTranslated = translateTopLabel(descriptions[i], t);
    exportData.push({ [topKeyTranslated]: tempVals });
  }

  
  const allowed = new Set<string>([...datalabels, ...translatedDatalabels]);
  const exportDataFiltered = exportData.filter((item: any) => {
    const key = Object.keys(item)[0];
    return allowed.has(key);
  });

  return exportDataFiltered;
};
